/**
 * The map annotation tool palette (ADR-034; feature-spec §8.1 / D16) — the
 * on-map toolbar of drawing tools, docked top-left of the map canvas.
 *
 * Renders the four drawing tools (Line / Circle / Polygon / Marker) as a
 * keyboard-operable toolbar wired to the ephemeral tool-state store
 * (:mod:`./annotation-tool-store`): clicking a tool activates it (`aria-pressed`
 * reflects the active tool); re-clicking the active tool deselects it (D14).
 * The active tool drives the map's Terra Draw mode + cursor. A Delete action
 * removes the selected annotation; selecting an annotation uses the default map
 * cursor (no tool active), not a dedicated tool.
 *
 * Accessibility (REQ-UI-106 / REQ-UI-059): a `role="toolbar"` of real
 * `<button>`s with per-tool `aria-label` + `aria-pressed`; focus-visible ring;
 * the icons are `aria-hidden` (the label is the accessible name).
 *
 * Cross-references:
 *   - ADR-034; feature-spec §8.1 (palette) / §8.2 (actions) / D14 / D16.
 *   - ./annotation-tool-store (the active tool + selectTool toggle).
 */
import type { ComponentType } from "react";
import {
  Circle,
  PencilSimple,
  LineSegment,
  MapPin,
  Polygon,
  Trash,
  type IconProps,
} from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { useAnnotationDeleteConfirmStore } from "@/annotations/annotation-delete-confirm-store";
import { AnnotationDeleteConfirmDialog } from "@/annotations/annotation-delete-confirm-dialog";
import { useAnnotationToolStore, type AnnotationTool } from "@/annotations/annotation-tool-store";
import { useAnnotationKeyboard } from "@/annotations/use-annotation-keyboard";

interface ToolDef {
  readonly tool: AnnotationTool;
  readonly label: string;
  readonly Icon: ComponentType<IconProps>;
}

/** The annotation tools, top-to-bottom: the Edit (geometry) tool then the draw
 *  tools. Selecting an annotation for config uses the default map cursor (no
 *  tool active) — there is no Select tool. */
const TOOLS: readonly ToolDef[] = [
  { tool: "edit", label: "Edit shape", Icon: PencilSimple },
  { tool: "line", label: "Line", Icon: LineSegment },
  { tool: "circle", label: "Circle", Icon: Circle },
  { tool: "polygon", label: "Polygon", Icon: Polygon },
  { tool: "marker", label: "Marker", Icon: MapPin },
];

/** Shared square-button base for the palette tools + actions. */
const BUTTON_CLASS =
  "flex h-8 w-8 items-center justify-center rounded text-text-secondary " +
  "hover:bg-bg-elevated focus:outline-none focus-visible:ring-1 focus-visible:ring-brand " +
  "disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent";

/** The annotation tool palette. Render inside the (relative) map container. */
export function AnnotationPalette(): React.ReactElement {
  const activeTool = useAnnotationToolStore((state) => state.activeTool);
  const selectTool = useAnnotationToolStore((state) => state.selectTool);
  const selectedId = useAnnotationToolStore((state) => state.selectedId);
  const requestDelete = useAnnotationDeleteConfirmStore((state) => state.requestDelete);

  // Keyboard affordances: Escape clears the tool/selection; Delete removes the
  // selected annotation (REQ-UI-059 keyboard operability).
  useAnnotationKeyboard();

  const handleDelete = (): void => {
    if (selectedId === null) {
      return;
    }
    // Open the confirmation dialog rather than deleting outright (#6).
    requestDelete(selectedId);
  };

  return (
    <div
      role="toolbar"
      aria-label="Map annotation tools"
      data-testid="annotation-palette"
      className="absolute left-3 top-3 z-10 flex flex-col gap-1 rounded border border-border bg-bg-surface p-1 shadow-md"
    >
      {TOOLS.map(({ tool, label, Icon }) => {
        const active = activeTool === tool;
        return (
          <button
            key={tool}
            type="button"
            aria-pressed={active}
            aria-label={label}
            title={label}
            data-testid={`annotation-tool-${tool}`}
            onClick={() => selectTool(tool)}
            className={cn(BUTTON_CLASS, active && "bg-brand text-bg-base")}
          >
            <Icon size={18} aria-hidden />
          </button>
        );
      })}

      <div className="my-0.5 h-px bg-border" aria-hidden />

      <button
        type="button"
        aria-label="Delete selected annotation"
        title="Delete selected"
        data-testid="annotation-delete"
        disabled={selectedId === null}
        onClick={handleDelete}
        className={cn(BUTTON_CLASS, "hover:text-destructive")}
      >
        <Trash size={18} aria-hidden />
      </button>
      <AnnotationDeleteConfirmDialog />
    </div>
  );
}
